import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

function proj(x: number, y: number, z: number, ox = 50, oy = 120): [number, number] {
  return [ox + x - y * 0.38, oy - z + y * 0.32];
}

export function Clone3dDiagram({ values }: DiagramProps) {
  const count = values.count ?? [3, 3, 3];
  const spacing = values.spacing ?? [10, 10, 10];
  const cx = Math.max(1, Math.min(4, Math.round(count[0] ?? 3)));
  const cy = Math.max(1, Math.min(4, Math.round(count[1] ?? 3)));
  const cz = Math.max(1, Math.min(4, Math.round(count[2] ?? 3)));
  const spX = spacing[0] ?? 10;
  const spY = spacing[1] ?? 10;
  const spZ = spacing[2] ?? 10;

  const stepX = 30, stepY = 24, stepZ = 22;
  const boxW = 10, boxD = 8, boxH = 8;

  const boxes: [number, number, number][] = [];
  for (let iz = 0; iz < cz; iz++)
    for (let iy = 0; iy < cy; iy++)
      for (let ix = 0; ix < cx; ix++)
        boxes.push([ix, iy, iz]);

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="3D Clone diagram">
      <defs>
        <marker id="arr-cl3x" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
          <path d="M0,0.5 L4,2.5 L0,4.5 Z" fill={D.X_COLOR} />
        </marker>
        <marker id="arr-cl3y" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
          <path d="M0,0.5 L4,2.5 L0,4.5 Z" fill={D.Y_COLOR} />
        </marker>
        <marker id="arr-cl3z" markerWidth="5" markerHeight="5" refX="4" refY="2.5" orient="auto">
          <path d="M0,0.5 L4,2.5 L0,4.5 Z" fill={D.Z_COLOR} />
        </marker>
      </defs>

      {/* Draw boxes (back-to-front for painter's algorithm) */}
      {[...boxes].reverse().map(([ix, iy, iz]) => {
        const isOrigin = ix === 0 && iy === 0 && iz === 0;
        const [bx, by] = proj(ix * stepX, iy * stepY, iz * stepZ);
        const [tx, ty] = proj(ix * stepX + boxW, iy * stepY, iz * stepZ);
        const [lx, ly] = proj(ix * stepX, iy * stepY + boxD, iz * stepZ);
        const [t2x, t2y] = proj(ix * stepX + boxW, iy * stepY, iz * stepZ + boxH);
        const [t3x, t3y] = proj(ix * stepX, iy * stepY, iz * stepZ + boxH);
        const [t4x, t4y] = proj(ix * stepX + boxW, iy * stepY + boxD, iz * stepZ + boxH);

        const frontPts = [
          proj(ix*stepX,      iy*stepY, iz*stepZ),
          proj(ix*stepX+boxW, iy*stepY, iz*stepZ),
          proj(ix*stepX+boxW, iy*stepY, iz*stepZ+boxH),
          proj(ix*stepX,      iy*stepY, iz*stepZ+boxH),
        ].map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

        const topPts = [
          proj(ix*stepX,      iy*stepY,      iz*stepZ+boxH),
          proj(ix*stepX+boxW, iy*stepY,      iz*stepZ+boxH),
          proj(ix*stepX+boxW, iy*stepY+boxD, iz*stepZ+boxH),
          proj(ix*stepX,      iy*stepY+boxD, iz*stepZ+boxH),
        ].map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

        const sidePts = [
          proj(ix*stepX+boxW, iy*stepY,      iz*stepZ),
          proj(ix*stepX+boxW, iy*stepY+boxD, iz*stepZ),
          proj(ix*stepX+boxW, iy*stepY+boxD, iz*stepZ+boxH),
          proj(ix*stepX+boxW, iy*stepY,      iz*stepZ+boxH),
        ].map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

        const fill = isOrigin ? D.GEO_FILL : 'rgba(96,165,250,0.05)';
        const sw = isOrigin ? '1.2' : '0.8';
        const dash = isOrigin ? 'none' : '2 2';

        return (
          <g key={`${ix}-${iy}-${iz}`}>
            <polygon points={frontPts} fill={fill} stroke={D.GEO_STROKE} strokeWidth={sw} strokeDasharray={dash} />
            <polygon points={topPts} fill="rgba(96,165,250,0.1)" stroke={D.GEO_STROKE} strokeWidth={sw} strokeDasharray={dash} />
            <polygon points={sidePts} fill="rgba(96,165,250,0.03)" stroke={D.GEO_STROKE} strokeWidth={sw} strokeDasharray={dash} />
          </g>
        );
      })}

      {/* Axis labels */}
      {(() => {
        const endX = proj(cx * stepX + 8, 0, 0);
        const endY = proj(0, cy * stepY + 8, 0);
        const endZ = proj(0, 0, cz * stepZ + 8);
        return (
          <>
            <line x1={proj(0,0,0)[0]} y1={proj(0,0,0)[1]}
                  x2={endX[0]} y2={endX[1]}
                  stroke={D.X_COLOR} strokeWidth="1.2" markerEnd="url(#arr-cl3x)" />
            <text x={endX[0]+4} y={endX[1]+3} fontSize="9" fontFamily="monospace" fill={D.X_COLOR} opacity="0.85">X×{cx}</text>
            <line x1={proj(0,0,0)[0]} y1={proj(0,0,0)[1]}
                  x2={endY[0]} y2={endY[1]}
                  stroke={D.Y_COLOR} strokeWidth="1.2" markerEnd="url(#arr-cl3y)" />
            <text x={endY[0]-4} y={endY[1]+10} fontSize="9" fontFamily="monospace" fill={D.Y_COLOR} opacity="0.85">Y×{cy}</text>
            <line x1={proj(0,0,0)[0]} y1={proj(0,0,0)[1]}
                  x2={endZ[0]} y2={endZ[1]}
                  stroke={D.Z_COLOR} strokeWidth="1.2" markerEnd="url(#arr-cl3z)" />
            <text x={endZ[0]-10} y={endZ[1]-4} fontSize="9" fontFamily="monospace" fill={D.Z_COLOR} opacity="0.85">Z×{cz}</text>
          </>
        );
      })()}

      <text x="8" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45">
        count=[{count[0]},{count[1]},{count[2]}]  spacing=[{spX},{spY},{spZ}]
      </text>
    </svg>
  );
}
